import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from './shared/RichTextContent';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Preset icons ────────────────────────────────────────────────────────────
// Each benefit card is paired with a preset SVG so the section stays visually
// consistent as authors edit the copy. The set matches the three canonical
// perks of a Preferred Employer Program: waived application fee, waived
// administration fee, and total savings callout.

const ICON_MAP = {
  waived: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  discount: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20.59 13.41L13.41 20.59a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.83z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="7" r="1.5" fill="currentColor" />
    </svg>
  ),
  savings: (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3l7 3v5.5c0 4.4-3 8.2-7 9.5-4-1.3-7-5.1-7-9.5V6l7-3z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
} as const;

export function PepBenefits() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section ref={sectionRef} className="reveal-fade-up pep-benefits">
      <div className="pep-benefits-container">
        <div className="pep-benefits-header">
          <RichTextContent
            path="pepbenefits.heading"
            defaultValue="<p>Preferred Employee Benefits</p>"
            as="h2"
            className="pep-benefits-heading"
          />
        </div>

        <div className="pep-benefits-grid">
          {/* Benefit 1 — Waived Application Fee */}
          <article className="pep-benefit-card">
            <div className="pep-benefit-icon" aria-hidden="true">{ICON_MAP.waived}</div>
            <RichTextContent
              path="pepbenefits.benefit1_title"
              defaultValue="<p>Waived Application Fee</p>"
              as="h3"
              className="pep-benefit-title"
            />
            <RichTextContent
              path="pepbenefits.benefit1_description"
              defaultValue="<p>($60 Value)</p>"
              as="div"
              className="pep-benefit-value"
            />
          </article>

          {/* Benefit 2 — Waived Administration Fee */}
          <article className="pep-benefit-card">
            <div className="pep-benefit-icon" aria-hidden="true">{ICON_MAP.discount}</div>
            <RichTextContent
              path="pepbenefits.benefit2_title"
              defaultValue="<p>Waived Administration Fee</p>"
              as="h3"
              className="pep-benefit-title"
            />
            <RichTextContent
              path="pepbenefits.benefit2_description"
              defaultValue="<p>($200 Value)</p>"
              as="div"
              className="pep-benefit-value"
            />
          </article>

          {/* Benefit 3 — Total Savings */}
          <article className="pep-benefit-card">
            <div className="pep-benefit-icon" aria-hidden="true">{ICON_MAP.savings}</div>
            <RichTextContent
              path="pepbenefits.benefit3_title"
              defaultValue="<p>Total Savings</p>"
              as="h3"
              className="pep-benefit-title"
            />
            <RichTextContent
              path="pepbenefits.benefit3_description"
              defaultValue="<p>$260!</p>"
              as="div"
              className="pep-benefit-value"
            />
          </article>
        </div>
      </div>

      <style>{`
        .pep-benefits {
          padding: 4rem 0 4.5rem;
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .pep-benefits { padding: 5.5rem 0 6rem; }
        }

        .pep-benefits-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .pep-benefits-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .pep-benefits-container { padding: 0 2rem; } }

        .pep-benefits-header {
          text-align: center;
          max-width: 42rem;
          margin: 0 auto 3rem;
        }

        .pep-benefits-heading {
          font-family: var(--vibe-font-heading);
          font-size: 2rem;
          font-weight: 700;
          line-height: 1.15;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          margin: 0;
        }
        @media (min-width: 768px) {
          .pep-benefits-heading { font-size: 2.5rem; }
        }

        .pep-benefits-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.25rem;
          max-width: 60rem;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .pep-benefits-grid { grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
        }

        .pep-benefit-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2rem 1.75rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s, border-color 0.25s;
        }
        .pep-benefit-card:hover {
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 30%, #e8e8ea);
        }

        .pep-benefit-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: color-mix(in srgb, var(--vibe-color-secondary) 12%, transparent);
          color: var(--vibe-color-secondary);
          margin-bottom: 1.5rem;
        }

        .pep-benefit-title {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.3;
          color: var(--vibe-color-text);
          margin: 0 0 0.5rem;
        }

        .pep-benefit-value {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 600;
          color: var(--vibe-color-text);
        }
        .pep-benefit-value :where(p) {
          margin: 0;
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ──────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PepBenefits',
  displayName: 'PEP – Benefits Grid',
  tokens: {
    'pepbenefits.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Preferred Employee Benefits</p>',
    },
    'pepbenefits.benefit1_title': {
      label: 'Benefit 1 Title',
      type: 'richtext',
      defaultValue: '<p>Waived Application Fee</p>',
    },
    'pepbenefits.benefit1_description': {
      label: 'Benefit 1 Description',
      type: 'richtext',
      defaultValue: '<p>($60 Value)</p>',
    },
    'pepbenefits.benefit2_title': {
      label: 'Benefit 2 Title',
      type: 'richtext',
      defaultValue: '<p>Waived Administration Fee</p>',
    },
    'pepbenefits.benefit2_description': {
      label: 'Benefit 2 Description',
      type: 'richtext',
      defaultValue: '<p>($200 Value)</p>',
    },
    'pepbenefits.benefit3_title': {
      label: 'Benefit 3 Title',
      type: 'richtext',
      defaultValue: '<p>Total Savings</p>',
    },
    'pepbenefits.benefit3_description': {
      label: 'Benefit 3 Description',
      type: 'richtext',
      defaultValue: '<p>$260!</p>',
    },
  },
};

(PepBenefits as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PepBenefits;
